import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Github, Plus, Upload, Loader2, ExternalLink, Check, AlertCircle, FolderGit2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getExt(lang) {
  if (lang === 'javascript' || lang === 'js') return 'js';
  if (lang === 'jsx') return 'jsx';
  if (lang === 'python' || lang === 'py') return 'py';
  if (lang === 'html') return 'html';
  if (lang === 'css') return 'css';
  return 'txt';
}

function extractCode(msgs) {
  if (!msgs) return [];
  const files = [];
  const regex = /```(\w+)?\n([\s\S]*?)```/g;
  for (let i = 0; i < msgs.length; i++) {
    if (msgs[i].role !== 'assistant') continue;
    const content = msgs[i].content;
    let match;
    let fi = 0;
    while ((match = regex.exec(content)) !== null) {
      const code = match[2].trim();
      if (code.length > 10) {
        files.push({ path: 'src/code_' + i + '_' + fi + '.' + getExt(match[1]), content: code });
        fi++;
      }
    }
  }
  return files;
}

export function GitHubPushModal(props) {
  const { isOpen, onClose, project, conversation } = props;
  const [step, setStep] = useState('choose');
  const [loading, setLoading] = useState(false);
  const [repos, setRepos] = useState([]);
  const [selRepo, setSelRepo] = useState(null);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [priv, setPriv] = useState(false);
  const [result, setResult] = useState(null);
  const [connected, setConnected] = useState(null);

  useEffect(function() {
    if (!isOpen) return;
    axios.get(API + '/github/status').then(function(r) {
      setConnected(r.data.connected);
      if (r.data.connected) {
        axios.get(API + '/github/repos').then(function(rr) {
          setRepos(rr.data.repositories);
        });
      }
    }).catch(function() {
      setConnected(false);
    });
    if (project && project.name) {
      setName(project.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
      setDesc(project.name + ' - CloudTexa AI');
    }
  }, [isOpen, project]);

  function createRepo() {
    if (!name.trim()) {
      toast.error('Ingresa nombre');
      return;
    }
    setLoading(true);
    axios.post(API + '/github/repos/create', {
      name: name,
      description: desc,
      private: priv,
      project_id: project ? project.id : null
    }).then(function(r) {
      toast.success('Repositorio creado');
      setSelRepo(r.data.repository);
      setStep('push');
      setLoading(false);
    }).catch(function(e) {
      toast.error(e.response && e.response.data ? e.response.data.detail : 'Error');
      setLoading(false);
    });
  }

  function pushCode() {
    if (!selRepo) return;
    const files = extractCode(conversation);
    if (files.length === 0) {
      toast.error('No hay código');
      return;
    }
    setLoading(true);
    axios.post(API + '/github/repos/push-multiple', {
      repo_full_name: selRepo.full_name,
      files: files,
      message: 'CloudTexa AI: ' + (project ? project.name : 'code')
    }).then(function(r) {
      setResult(r.data);
      toast.success(r.data.results.length + ' archivos subidos');
      setLoading(false);
    }).catch(function(e) {
      toast.error(e.response && e.response.data ? e.response.data.detail : 'Error');
      setLoading(false);
    });
  }

  if (!isOpen) return null;

  function renderRepoList() {
    const items = [];
    for (let i = 0; i < repos.length && i < 6; i++) {
      const r = repos[i];
      items.push(
        <button key={r.id} onClick={function() { setSelRepo(r); setStep('push'); }} className="w-full flex items-center gap-3 p-3 rounded-lg border border-white/10 hover:bg-white/5 text-left">
          <FolderGit2 className="w-5 h-5 text-gray-400" />
          <span className="text-white">{r.name}</span>
        </button>
      );
    }
    return items;
  }

  function renderResultFiles() {
    if (!result || !result.results) return null;
    const items = [];
    for (let i = 0; i < result.results.length; i++) {
      const f = result.results[i];
      items.push(
        <div key={i} className="flex justify-between py-1">
          <span className="text-white text-sm">{f.path}</span>
          <a href={f.url} target="_blank" rel="noreferrer" className="text-[#FF6B00]">
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      );
    }
    return items;
  }

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="w-full max-w-lg glass rounded-2xl" onClick={function(e) { e.stopPropagation(); }}>
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <Github className="w-6 h-6 text-white" />
              <h2 className="text-xl font-bold text-white">Subir a GitHub</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">×</button>
          </div>
          <div className="p-6">
            {connected === false && (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                <p className="text-white mb-2">GitHub no conectado</p>
                <p className="text-gray-400 text-sm">Conecta desde el menú de usuario.</p>
              </div>
            )}
            
            {connected && result && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-green-400">
                  <Check className="w-6 h-6" />
                  <span>Código subido</span>
                </div>
                <div className="glass rounded-xl p-4 border border-white/10">
                  {renderResultFiles()}
                </div>
                <a href={result.repo_url} target="_blank" rel="noreferrer" className="block w-full py-3 rounded-xl bg-[#FF6B00] text-white text-center">Ver en GitHub</a>
              </div>
            )}
            
            {connected && !result && step === 'choose' && (
              <div className="space-y-4">
                <button onClick={function() { setStep('create'); }} className="w-full flex items-center gap-4 p-4 rounded-xl border border-dashed border-white/20 hover:border-[#FF6B00]/50">
                  <div className="w-10 h-10 rounded-lg bg-[#FF6B00]/20 flex items-center justify-center">
                    <Plus className="w-5 h-5 text-[#FF6B00]" />
                  </div>
                  <div className="text-left">
                    <p className="text-white">Crear nuevo</p>
                    <p className="text-sm text-gray-500">Nuevo repositorio</p>
                  </div>
                </button>
                {renderRepoList()}
              </div>
            )}
            
            {connected && !result && step === 'create' && (
              <div className="space-y-4">
                <button onClick={function() { setStep('choose'); }} className="text-sm text-gray-400">← Volver</button>
                <input value={name} onChange={function(e) { setName(e.target.value.toLowerCase().replace(/\s+/g, '-')); }} placeholder="nombre-repo" className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white" />
                <input value={desc} onChange={function(e) { setDesc(e.target.value); }} placeholder="Descripción" className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white" />
                <label className="flex items-center gap-3">
                  <input type="checkbox" checked={priv} onChange={function(e) { setPriv(e.target.checked); }} />
                  <span className="text-gray-400 text-sm">Privado</span>
                </label>
                <button onClick={createRepo} disabled={loading} className="w-full py-3 rounded-xl bg-[#FF6B00] text-white flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                  Crear
                </button>
              </div>
            )}
            
            {connected && !result && step === 'push' && (
              <div className="space-y-4">
                <button onClick={function() { setStep('choose'); setSelRepo(null); }} className="text-sm text-gray-400">← Volver</button>
                <div className="glass rounded-xl p-4 border border-white/10 flex items-center gap-2">
                  <Github className="w-5 h-5" />
                  <span className="text-white">{selRepo ? selRepo.full_name : ''}</span>
                </div>
                <button onClick={pushCode} disabled={loading} className="w-full py-3 rounded-xl bg-[#FF6B00] text-white flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  Subir Código
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
